/**
 * DM-13 — `/docs/$role/$slug` — страница чтения.
 */

import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { Edit3, History as HistoryIcon } from 'lucide-react';
import { useEffect, useRef } from 'react';
import {
  DocsForbidden,
  DocsLayout,
  DocsNotFound,
  DocsParseError,
  DocsReaderMeta,
  DocsSkeleton,
  DocsUpdatedBanner,
  DocsViewer,
} from '~/components/docs';
import { ApiRequestError } from '~/lib/api/client';
import { fetchDocPage, fetchDocsToc, markDocRead } from '~/lib/api/docs';
import { useAuthStore } from '~/store/auth';

export const Route = createFileRoute('/_auth/docs/$role/$slug/')({
  component: DocsPageRoute,
});

function DocsPageRoute(): JSX.Element {
  const params = useParams({ from: '/_auth/docs/$role/$slug/' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const userRole = useAuthStore((s) => s.user?.role ?? 'viewer');
  const isAdmin = userRole === 'admin';

  const tocQuery = useQuery({
    queryKey: ['docs', 'toc', params.role, 'ru'],
    queryFn: () => fetchDocsToc({ role: params.role, locale: 'ru' }),
  });

  const pageQuery = useQuery({
    queryKey: ['docs', 'page', params.role, params.slug, 'ru'],
    queryFn: () => fetchDocPage(params.role, params.slug, { locale: 'ru' }),
    retry: false,
  });

  const readMutation = useMutation({
    mutationFn: () =>
      markDocRead(params.role, params.slug, pageQuery.data!.version, { locale: 'ru' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['docs', 'toc'] });
    },
  });

  // Авто mark-read через 2s после загрузки страницы (debounced).
  const markedRef = useRef(false);
  const markMutate = readMutation.mutate;
  useEffect(() => {
    if (!pageQuery.data || markedRef.current) return;
    if (!pageQuery.data.user_state.is_new_for_user) return;
    const t = setTimeout(() => {
      markMutate();
      markedRef.current = true;
    }, 2000);
    return () => clearTimeout(t);
  }, [pageQuery.data, markMutate]);

  const errorStatus =
    pageQuery.error instanceof ApiRequestError ? pageQuery.error.status : undefined;

  return (
    <DocsLayout
      toc={tocQuery.data}
      currentRole={params.role}
      currentSlug={params.slug}
      pageToc={pageQuery.data?.toc ?? []}
      breadcrumbs={[
        { label: 'Документация', href: '/docs' },
        { label: roleLabel(params.role), href: `/docs/${params.role}` },
        { label: pageQuery.data?.title ?? params.slug },
      ]}
      actions={
        pageQuery.data && isAdmin ? (
          <>
            <button
              type="button"
              className="docs-action-btn"
              onClick={() => {
                window.location.href = `/docs/${params.role}/${params.slug}/edit`;
              }}
            >
              <Edit3 size={14} /> {m.ui_redaktirovat()}
            </button>
            <button
              type="button"
              className="docs-action-btn"
              onClick={() => {
                window.location.href = `/docs/${params.role}/${params.slug}/history`;
              }}
            >
              <HistoryIcon size={14} /> {m.ui_istoriya()}
            </button>
          </>
        ) : null
      }
    >
      {pageQuery.isLoading ? (
        <DocsSkeleton />
      ) : errorStatus === 404 ? (
        <DocsNotFound onHome={() => navigate({ to: '/docs' })} />
      ) : errorStatus === 403 ? (
        <DocsForbidden />
      ) : pageQuery.error ? (
        <div style={{ padding: 'var(--space-5, 24px)' }}>
          {m.ui_ne_udalos_zagruzit_dokument_poprobuyte_obnovit_stranicu()}
        </div>
      ) : pageQuery.data ? (
        <>
          {pageQuery.data.is_fallback_locale ? (
            <div
              role="status"
              style={{
                margin: 'var(--space-3, 12px) var(--space-5, 24px) 0',
                padding: 'var(--space-2, 8px) var(--space-3, 12px)',
                background: 'var(--accent-bg-5, rgba(91,108,255,0.08))',
                fontSize: 'var(--text-sm, 14px)',
              }}
            >
              {m.ui_perevod_nedostupen_pokazan_russkiy_original()}
            </div>
          ) : null}
          {pageQuery.data.user_state.is_new_for_user &&
          pageQuery.data.user_state.last_read_version !== null ? (
            <DocsUpdatedBanner
              role={params.role}
              slug={params.slug}
              fromVersion={pageQuery.data.user_state.last_read_version}
              toVersion={pageQuery.data.version}
            />
          ) : null}
          <DocsReaderMeta
            bodyMd={pageQuery.data.body_md}
            updatedAt={pageQuery.data.updated_at}
            version={pageQuery.data.version}
          />
          {tryRenderViewer(pageQuery.data, isAdmin)}
        </>
      ) : null}
    </DocsLayout>
  );
}

type DocPage = Awaited<ReturnType<typeof fetchDocPage>>;
function tryRenderViewer(page: DocPage, isAdmin: boolean): JSX.Element {
  try {
    return <DocsViewer page={page} />;
  } catch {
    return <DocsParseError rawBody={page.body_md} isAdmin={isAdmin} />;
  }
}

function roleLabel(role: string): string {
  const map: Record<string, string> = {
    manager: 'Руководитель',
    accountant: 'Бухгалтер',
    reviewer: 'Старший бухгалтер',
    admin: 'Администратор',
    viewer: 'Аудитор',
    warehouse: 'Кладовщик',
    shared: 'Общие материалы',
  };
  return map[role] ?? role;
}
